import React from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import { LeadData } from "@/pages/leads/types";
import KPIsContainer from "@/components/controls/KPIsContainer";
import IcpFitKPICell from "@/components/controls/DetailedView/ICPFit/IcpFitKPICell";
import { IcpFitSubject } from "@/entities/icpFit/types";
import { differenceInDays } from "date-fns";
import { useIsDemoLead } from "@/assets/mockupData";
import { isEmpty, isNull, map } from "lodash";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";

interface Props {
  lead?: LeadData;
  containerClassName?: string;
  isQuickView?: boolean;
}

interface KPIConfig {
  [field: string]: {
    subtitle: string;
    formatter?: (value: number | string | null) => string | JSX.Element;
    icon?: (value: number) => JSX.Element;
  };
}

const LeadKPIs: React.FC<Props> = ({
  lead,
  containerClassName,
  isQuickView
}) => {
  const { t } = useTranslation();
  const { enabled: isIcpFitEnabled, loading: isIcpFitFlagLoading } =
    useFeatureFlag(FeatureFlags.CRM_5676_ICP);

  const isDemo = useIsDemoLead(lead?.id || "");

  if (!lead) {
    return null;
  }

  const kpiConfig: KPIConfig = {
    ...(isDemo
      ? {
          dreamerProbabilityToConvert: {
            subtitle: t("leads.kpis.dreamerProbabilityToConvert"),
            formatter: (_value) => "67%"
          }
        }
      : {}),
    openForDays: {
      subtitle: t("leads.kpis.openForDays"),
      formatter: (value) =>
        isNull(value) || isNaN(value as any)
          ? "-"
          : `${value} ${t(value === 1 ? "dateTime.day" : "dateTime.days")}`
    },
    lastActivity: {
      subtitle: t("leads.kpis.lastActivity"),
      formatter: (value) => {
        if (isEmpty(value)) {
          return "-";
        }
        const now = new Date();
        const daysDifference = differenceInDays(now, value as string);
        return `${daysDifference} ${t(daysDifference === 1 ? "dateTime.day" : "dateTime.days")}`;
      }
    }
  };

  const fieldKeys = [
    ...(isDemo ? ["dreamerProbabilityToConvert"] : []),
    "openForDays",
    "lastActivity"
  ];

  const showIcpFitKpis = isIcpFitEnabled && !isIcpFitFlagLoading;

  return (
    <KPIsContainer className={containerClassName}>
      {map(fieldKeys, (field) => {
        const fieldConfig = kpiConfig[field];
        const value = lead[field];
        return (
          <div key={field} className="flex flex-col gap-1 p-3">
            <div className="flex gap-1 items-center h-7">
              {fieldConfig.icon &&
                typeof value === "number" &&
                fieldConfig.icon?.(value as number)}
              <Text as="span" variant={isQuickView ? "lgLabel" : "smSubtitle"}>
                {fieldConfig.formatter && fieldConfig.formatter?.(value)}
              </Text>
            </div>
            <Text variant={"bodySecondary"}>{fieldConfig.subtitle}</Text>
          </div>
        );
      })}
      {showIcpFitKpis && (
        <IcpFitKPICell
          key="contactIcpFit"
          subtitle={t("leads.kpis.contactIcpFit")}
          subject={IcpFitSubject.PERSON}
          value={lead.contactIcpFit}
          bullets={lead.contactIcpMetadata?.bullets}
          isQuickView={isQuickView}
        />
      )}
      {showIcpFitKpis && (
        <IcpFitKPICell
          key="companyIcpFit"
          subtitle={t("leads.kpis.companyIcpFit")}
          subject={IcpFitSubject.COMPANY}
          value={lead.companyIcpFit}
          bullets={lead.companyIcpMetadata?.bullets}
          isQuickView={isQuickView}
        />
      )}
    </KPIsContainer>
  );
};

export default LeadKPIs;
